import { type ReactNode } from 'react';
import { ChevronRight, type LucideIcon } from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle, cn } from '../../ui';

/**
 * Shared presentational panels for the Personal / Team home pages. Purely
 * presentational — no data metrics, just titled sections + clickable rows that
 * deep-link to the underlying entities.
 */

export interface HomePanelProps {
  title: string;
  icon: LucideIcon;
  /** Optional count shown next to the title. */
  count?: number;
  /** Optional right-aligned header content (badge / link). */
  headerRight?: ReactNode;
  /** Body content (rows), or the empty message when there is nothing. */
  children?: ReactNode;
  /** Shown (muted) when there is nothing to list. */
  emptyLabel?: string;
  /** When true, renders {@link emptyLabel} instead of children. */
  empty?: boolean;
  className?: string;
}

/** A titled section card used across both homes. */
export function HomePanel({
  title,
  icon: Icon,
  count,
  headerRight,
  children,
  emptyLabel = 'Nothing here right now.',
  empty,
  className,
}: HomePanelProps) {
  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-accent" aria-hidden="true" />
          {title}
          {count !== undefined && count > 0 && (
            <span className="rounded-full bg-surface-3 px-2 py-0.5 text-[11px] font-medium text-zinc-400">
              {count}
            </span>
          )}
        </CardTitle>
        {headerRight}
      </CardHeader>
      <CardBody className={cn('space-y-1', empty && 'py-4')}>
        {empty ? (
          <p className="text-sm text-zinc-500">{emptyLabel}</p>
        ) : (
          children
        )}
      </CardBody>
    </Card>
  );
}

export interface LinkRowProps {
  primary: ReactNode;
  secondary?: ReactNode;
  /** Right-aligned meta (badge, date, etc.). */
  trailing?: ReactNode;
  onClick?: () => void;
}

/** A single clickable row inside a {@link HomePanel}. */
export function LinkRow({ primary, secondary, trailing, onClick }: LinkRowProps) {
  const clickable = !!onClick;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!clickable}
      className={cn(
        'group flex w-full items-center gap-3 rounded-lg px-2 py-1.5 text-left',
        clickable
          ? 'transition-colors hover:bg-surface-3'
          : 'cursor-default',
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm text-zinc-200">{primary}</div>
        {secondary && (
          <div className="truncate text-xs text-zinc-500">{secondary}</div>
        )}
      </div>
      {trailing}
      {clickable && (
        <ChevronRight
          className="h-4 w-4 shrink-0 text-zinc-600 transition-colors group-hover:text-zinc-400"
          aria-hidden="true"
        />
      )}
    </button>
  );
}

export interface StatTileProps {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: 'default' | 'accent' | 'success' | 'warning' | 'danger';
}

const STAT_TONE: Record<NonNullable<StatTileProps['tone']>, string> = {
  default: 'text-zinc-100',
  accent: 'text-accent-fg',
  success: 'text-emerald-300',
  warning: 'text-amber-300',
  danger: 'text-red-300',
};

/** A compact metric tile (delivery/awareness signal — never a person score). */
export function StatTile({ label, value, hint, tone = 'default' }: StatTileProps) {
  return (
    <Card>
      <CardBody className="py-4">
        <p className="text-[11px] font-medium uppercase tracking-wide text-zinc-500">
          {label}
        </p>
        <p className={cn('mt-1 text-2xl font-semibold', STAT_TONE[tone])}>
          {value}
        </p>
        {hint && <p className="mt-0.5 text-xs text-zinc-500">{hint}</p>}
      </CardBody>
    </Card>
  );
}
